/**
 * PrepaidUntilFields — ช่อง "ชำระล่วงหน้าถึงวันที่" + หมายเหตุ ของสัญญาเช่า (ใช้ทั้งหน้าสร้าง/แก้ไข)
 *
 * ที่มา (ไฟล์สรุปวางบิลของฝ่ายการเงิน ก.ย. 2026): ลูกค้า 3 ราย (COM00081/COM00097/COM00082) ชำระค่าเช่าล่วงหน้า
 * 11–24 เดือน แต่ระบบไม่รู้จึงออกใบแจ้งหนี้ซ้ำทุกเดือน → ระบุวันที่ที่ชำระครอบคลุมถึง แล้วรอบวางบิลจะข้ามสัญญานี้
 *
 * - ว่าง = ไม่ได้ชำระล่วงหน้า (ปกติทุกสัญญา) · ปุ่ม "ล้างค่า" คืนเป็นว่าง
 * - ช่องวันที่ไม่ยอมให้เลือกก่อนวันเริ่มเก็บเงิน (min) — backend ตรวจซ้ำอีกชั้น
 */
import React from 'react';
import { RiCalendarLine } from 'react-icons/ri';
import { cn } from '../../utils/cn';

export const PREPAID_NOTE_MAX_LENGTH = 500;

export interface PrepaidUntilFieldsProps {
  prepaidUntil: string;
  prepaidNote: string;
  onChange: (next: { prepaid_until: string; prepaid_note: string }) => void;
  /** วันเริ่มเก็บเงินที่มีผลจริง (billing_start_date หรือ rental_date) — ใช้เป็น min ของช่องวันที่ */
  minDate?: string;
  disabled?: boolean;
  error?: string;
}

const PrepaidUntilFields: React.FC<PrepaidUntilFieldsProps> = ({
  prepaidUntil,
  prepaidNote,
  onChange,
  minDate,
  disabled,
  error,
}) => (
  <div data-testid="prepaid-until-fields" className="sm:col-span-2 lg:col-span-6 rounded-lg border border-secondary-200 p-3">
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-6">
      <div className="lg:col-span-3">
        <label htmlFor="prepaid_until" className="block text-sm font-medium text-secondary-700">
          ชำระล่วงหน้าถึงวันที่
          <span
            title="ลูกค้าชำระค่าเช่าล่วงหน้าครอบคลุมถึงวันนี้ (รวมวันนี้) — รอบวางบิลที่อยู่ในช่วงนี้จะข้ามสัญญานี้ ไม่ออกใบแจ้งหนี้ซ้ำ"
            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-secondary-200 text-secondary-500 text-xs cursor-help"
          >
            ?
          </span>
        </label>
        <div className="flex gap-2 mt-1">
          <div className="relative flex-1">
            <RiCalendarLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5 pointer-events-none" />
            <input
              type="date"
              id="prepaid_until"
              name="prepaid_until"
              aria-label="ชำระล่วงหน้าถึงวันที่"
              value={prepaidUntil}
              min={minDate || undefined}
              onChange={(e) => onChange({ prepaid_until: e.target.value, prepaid_note: prepaidNote })}
              className={cn('input pl-10 w-full', error && 'border-danger-500 bg-danger-50', disabled && 'bg-secondary-50 cursor-not-allowed')}
              aria-invalid={Boolean(error)}
              disabled={disabled}
            />
          </div>
          {prepaidUntil && (
            <button
              type="button"
              onClick={() => onChange({ prepaid_until: '', prepaid_note: prepaidNote })}
              className="px-3 py-2 text-xs font-medium text-secondary-600 bg-secondary-100 border border-secondary-300 rounded-lg hover:bg-secondary-200 transition-colors whitespace-nowrap"
              title="ล้างค่า — ระบบจะวางบิลสัญญานี้ตามปกติ"
              disabled={disabled}
            >
              ล้างค่า
            </button>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-danger-600" role="alert">{error}</p>}
        <p className="mt-1 text-xs text-secondary-500">
          เว้นว่างได้ · ระบุเมื่อลูกค้าจ่ายค่าเช่าล่วงหน้าเป็นก้อน ระบบจะไม่ออกใบแจ้งหนี้ในรอบที่จ่ายแล้ว
        </p>
      </div>
      <div className="lg:col-span-3">
        <label htmlFor="prepaid_note" className="block text-sm font-medium text-secondary-700">
          หมายเหตุการชำระล่วงหน้า
        </label>
        <input
          type="text"
          id="prepaid_note"
          name="prepaid_note"
          value={prepaidNote}
          maxLength={PREPAID_NOTE_MAX_LENGTH}
          onChange={(e) => onChange({ prepaid_until: prepaidUntil, prepaid_note: e.target.value })}
          placeholder="เช่น ชำระแล้ว 68,544 บาท 05/01/2569 KIV2026000020 (หัก ณ ที่จ่าย 5%)"
          className={cn('input mt-1 w-full', disabled && 'bg-secondary-50 cursor-not-allowed')}
          disabled={disabled}
        />
        <p className="mt-1 text-xs text-secondary-500">ยอด · วันที่ชำระ · เลขที่ใบเสร็จ · หัก ณ ที่จ่าย (แสดงในไฟล์วางบิลของการเงิน)</p>
      </div>
    </div>
  </div>
);

export default PrepaidUntilFields;
